import React, { useEffect, useMemo, useState } from "react";
import { Search, Folder, Image as ImageIcon, X, Plus, Loader, Save } from "lucide-react";
import API_BASE_URL from "../../../services/api";
import Button from "../../../components/ui/Button";
import { canAccess } from "../../../utils/permissions";
import { useAuth } from "../../../context/AuthContext";
import axiosInstance from "../../../utils/axiosInstance";

const Department = () => {
  const { user: currentUser } = useAuth();

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [editingDepartment, setEditingDepartment] = useState(null);

  // ✅ matches Prisma model: name, type, sortOrder, isActive
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    sortOrder: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`${API_BASE_URL}/api/departments`);

      // ✅ controller returns { success, items, total... }
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.items)
          ? res.data.items
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];

      setDepartments(list);
    } catch (error) {
      console.error("Error fetching departments:", error);
      alert("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Department name is required";
    else if (formData.name.trim().length < 2)
      newErrors.name = "Department name must be at least 2 characters";

    if (!formData.type) newErrors.type = "Department type is required";

    if (formData.sortOrder !== "" && Number.isNaN(Number(formData.sortOrder))) {
      newErrors.sortOrder = "Sort order must be a number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      name: "",
      type: "",
      sortOrder: "",
      isActive: true,
    });
    setEditingDepartment(null);
    setErrors({});
  };

  const handleEdit = (dept) => {
    setEditingDepartment(dept);
    setFormData({
      name: dept?.name || "",
      type: dept?.type || "",
      sortOrder: dept?.sortOrder ?? "",
      isActive: dept?.isActive ?? true,
    });
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Department?")) return;

    try {
      await axiosInstance.delete(`${API_BASE_URL}/api/departments/${id}`);
      await fetchDepartments();
    } catch (error) {
      console.error("Error deleting department:", error);

      const msg = error?.response?.data?.message || "Failed to delete department";
      const usage = error?.response?.data?.usage;

      if (usage?.length) {
        alert(`${msg}\n\nUsed in:\n- ${usage.map((u) => `${u.label}: ${u.count}`).join("\n- ")}`);
      } else {
        alert(msg);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        type: formData.type, // PATHOLOGY | RADIOLOGY
        isActive: !!formData.isActive,
        sortOrder:
          formData.sortOrder === "" || formData.sortOrder === null || formData.sortOrder === undefined
            ? 0
            : Number(formData.sortOrder),
      };

      if (editingDepartment) {
        await axiosInstance.put(
          `${API_BASE_URL}/api/departments/${editingDepartment.id}`,
          payload
        );
      } else {
        await axiosInstance.post(`${API_BASE_URL}/api/departments`, payload);
      }

      resetForm();
      await fetchDepartments();
    } catch (error) {
      console.error("Error saving department:", error);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        `Failed to ${editingDepartment ? "update" : "create"} department`;
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredDepartments = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return departments;

    return departments.filter((d) => {
      return (
        d?.name?.toLowerCase().includes(q) ||
        d?.type?.toLowerCase().includes(q) ||
        String(d?.sortOrder ?? "").toLowerCase().includes(q) ||
        (d?.isActive ? "active".includes(q) : "inactive".includes(q))
      );
    });
  }, [departments, searchTerm]);

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "PATHOLOGY":
        return "bg-blue-100 text-blue-800";
      case "RADIOLOGY":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeDisplayName = (type) => {
    switch (type) {
      case "PATHOLOGY":
        return "Pathology";
      case "RADIOLOGY":
        return "Radiology";
      default:
        return type || "Not Set";
    }
  };

  return (
    <div className="">
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Departments</h1>
        <p className="text-gray-600 text-sm mt-0.5">Manage your medical service departments</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left Side - Add/Edit Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {editingDepartment ? "Edit Department" : "Create Department"}
              </h3>

              {editingDepartment && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Department Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Department Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, name: e.target.value }));
                    setErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  className={`w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.name ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="e.g., Hematology, X-Ray, Biochemistry"
                  disabled={submitting}
                />
                {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              </div>

              {/* Department Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Department Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, type: e.target.value }));
                    setErrors((prev) => ({ ...prev, type: undefined }));
                  }}
                  className={`w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.type ? "border-red-300" : "border-gray-300"
                  }`}
                  disabled={submitting}
                >
                  <option value="">Select Type</option>
                  <option value="PATHOLOGY">Pathology</option>
                  <option value="RADIOLOGY">Radiology</option>
                </select>
                {errors.type && <p className="mt-1 text-xs text-red-600">{errors.type}</p>}
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Sort Order <span className="text-gray-500 font-normal">(Optional)</span>
                </label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, sortOrder: e.target.value }));
                    setErrors((prev) => ({ ...prev, sortOrder: undefined }));
                  }}
                  className={`w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.sortOrder ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="0, 1, 2..."
                  disabled={submitting}
                />
                {errors.sortOrder && <p className="mt-1 text-xs text-red-600">{errors.sortOrder}</p>}
              </div>


              {/* Submit Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  variant="solid"
                  size="sm"
                  disabled={
                    !canAccess(currentUser, "canCreate") ||
                    submitting ||
                    !formData.name.trim() ||
                    !formData.type
                  }
                  loading={submitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-2"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-3 h-3 animate-spin mr-1.5" />
                      {editingDepartment ? "Updating..." : "Creating..."}
                    </>
                  ) : editingDepartment ? (
                    <>
                      <Save className="w-3 h-3 mr-1.5" />
                      Update Department
                    </>
                  ) : (
                    <>
                      <Plus className="w-3 h-3 mr-1.5" />
                      Create Department
                    </>
                  )}
                </Button>

                {editingDepartment && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={resetForm}
                    disabled={submitting}
                    className="px-4 border-gray-300 text-gray-700 hover:bg-gray-50 text-xs"
                  >
                    <X className="w-3 h-3 mr-1.5" />
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right Side - Departments List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">All Departments</h3>
                  <p className="text-gray-600 text-xs mt-0.5">
                    Total: {departments.length} • Showing: {filteredDepartments.length}
                  </p>
                </div>

                <div className="w-full sm:w-56">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <Search className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search departments..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-3">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
                  <p className="text-gray-600 text-xs font-medium">Loading departments...</p>
                </div>
              ) : filteredDepartments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Folder className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm font-medium mb-1">No departments found</p>
                  <p className="text-gray-400 text-xs">
                    {searchTerm ? "No departments match your search" : "Start by creating your first department"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredDepartments.map((dept, index) => (
                    <div
                      key={dept.id}
                      className={`p-3 border rounded-lg transition-all duration-200 ${
                        editingDepartment?.id === dept.id
                          ? "border-blue-300 bg-blue-50 ring-1 ring-blue-100"
                          : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-14 h-14 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-gray-400" />
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-1.5">
                            <div className="min-w-0">
                              <h3 className="text-xs font-semibold text-gray-900 truncate">
                                {dept?.name}
                              </h3>

                              <div
                                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getTypeBadgeColor(
                                  dept?.type
                                )}`}
                              >
                                {getTypeDisplayName(dept?.type)}
                              </div>

                              <div className="mt-2 flex items-center gap-2">
                                <span
                                  className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                    dept?.isActive
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                      : "bg-gray-50 text-gray-600 border-gray-200"
                                  }`}
                                >
                                  {dept?.isActive ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </div>

                            <span className="text-xs text-gray-400 font-medium bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                              #{index + 1}
                            </span>
                          </div>

                          <div className="flex items-center justify-between mt-3">
                            <div className="text-xs text-gray-500 truncate">
                              ID: <span className="font-mono text-xs">{dept.id || "N/A"}</span>
                              {"  "}•{"  "}
                              Sort: <span className="font-mono">{dept.sortOrder ?? 0}</span>
                            </div>

                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="xs"
                                iconName="Edit2"
                                disabled={!canAccess(currentUser, "canEdit")}
                                onClick={() => handleEdit(dept)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1"
                              />
                              <Button
                                variant="ghost"
                                size="xs"
                                iconName="Trash2"
                                disabled={!canAccess(currentUser, "canDelete")}
                                onClick={() => handleDelete(dept.id)}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Department;
